import { z } from 'zod/v4';

import type { InfinexMessageNamespace } from './base';
import { generateEventSchema } from './schemaBuilder';

import { evmChainIdSchema } from '../connect';
import { infinexUserBalancesSchema, infinexUserDataSchema } from '../userData';

export const InfinexOperationMethods = [
  'infinex-user-data',
  'perform-swidge',
  'supported-evm-networks',
  'user-balances',
] as const;
export type InfinexOperationMethod = (typeof InfinexOperationMethods)[number];
const namespace: InfinexMessageNamespace = 'infinex-operation';

export const performSwidgeTokenDataSchema = z
  .object({
    chain: z.string(),
    address: z.string(),
    assetAddress: z.string(),
  })
  .strict();
export type PerformSwidgeTokenData = z.infer<
  typeof performSwidgeTokenDataSchema
>;

export const walletInfoSchema = generateEventSchema({
  namespace,
  method: 'infinex-user-data',

  // request has no payload
  requestData: z.null(),
  // response is either `infinexUserDataSchema` or null
  responseData: z.union([infinexUserDataSchema, z.null()]),
  // disallow any notification
  notificationData: z.never(),
});

export const supportedNetworksSchema = generateEventSchema({
  namespace,
  method: 'supported-evm-networks',

  // request has no payload
  requestData: z.null(),
  // response is an array of chain IDs
  responseData: z.array(evmChainIdSchema),
  // disallow any notification
  notificationData: z.never(),
});

export const userBalancesSchema = generateEventSchema({
  namespace,
  method: 'user-balances',

  // request has no payload
  requestData: z.null(),
  // response is either `infinexUserBalancesSchema` or null
  responseData: z.union([infinexUserBalancesSchema, z.null()]),
  // disallow any notification
  notificationData: z.never(),
});

export const performSwidgeSchema = generateEventSchema({
  namespace,
  method: 'perform-swidge',

  // TODO: type this better
  requestData: performSwidgeTokenDataSchema,
  // did the swidge complete
  responseData: z.boolean(),
  // disallow any notification
  notificationData: z.never(),
});

/* ─────────────────── Aggregated schema arrays ─────────────────── */
export const infinexOpsRequestParamSchemas = [
  walletInfoSchema.requestParamsSchema,
  userBalancesSchema.requestParamsSchema,
  performSwidgeSchema.requestParamsSchema,
  supportedNetworksSchema.requestParamsSchema,
] as const;

export const infinexOpsResponseParamSchemas = [
  walletInfoSchema.responseParamsSchema,
  userBalancesSchema.responseParamsSchema,
  performSwidgeSchema.responseParamsSchema,
  supportedNetworksSchema.responseParamsSchema,
] as const;

export const infinexOpsNotificationParamSchemas = [] as const; // none

export const infinexOpsRequestEventSchemas = [
  walletInfoSchema.requestSchema,
  userBalancesSchema.requestSchema,
  performSwidgeSchema.requestSchema,
  supportedNetworksSchema.requestSchema,
] as const;
export const infinexOpsResponseEventSchemas = [
  walletInfoSchema.responseSchema,
  userBalancesSchema.responseSchema,
  performSwidgeSchema.responseSchema,
  supportedNetworksSchema.responseSchema,
] as const;
export const infinexOpsNotificationEventSchemas = [] as const; // none
